import React from 'react';
import { Link } from 'react-router-dom';
import { IconButton, Menu, Tooltip, Divider, Typography, Box, styled } from '@mui/material';
import { useActiveProject } from '../../../store/projectState';
import { useMenu } from '../../../components/base/useMenu';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import LaunchIcon from '@mui/icons-material/Launch';

const EmailMenuContainer = styled(Box)(({ theme }) => ({
  position: 'relative'
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    width: 280,
    borderRadius: '12px',
    padding: theme.spacing(1.5),
    boxShadow: theme.shadows[4],
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: -8,
      right: 14,
      width: 16,
      height: 16,
      backgroundColor: theme.palette.background.paper,
      transform: 'rotate(45deg)',
      zIndex: 0
    }
  }
}));

const EmailDisplayBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.action.hover,
  borderRadius: '6px',
  padding: theme.spacing(1),
  marginBottom: theme.spacing(1)
}));

const EmailText = styled(Typography)(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.8rem',
  wordBreak: 'break-all',
  color: theme.palette.text.primary
}));

const MenuItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1),
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

const EmailMenu = ({ theme }) => {
  const { triggerProps, menuProps } = useMenu();
  const activeProject = useActiveProject();
  const currentUrl = window.location.href;
  const isProduction = currentUrl.includes('app');

  const generateRandomString = (length) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let randomString = '';

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      randomString += characters.charAt(randomIndex);
    }

    return randomString;
  };

  const randomStringLength = 5;
  const randomString = generateRandomString(randomStringLength);
  const modifiedEmail = `${randomString}.${activeProject?.email_identifier}@${
    isProduction ? 'testmail.getscandium.com' : 'playmail.getscandium.com'
  }`;

  const copyEmailToClipBoard = () => {
    navigator.clipboard.writeText(modifiedEmail);
    toast.success('Copied to clipboard', {
      position: 'bottom-center',
      autoClose: 2000
    });
  };

  return (
    <EmailMenuContainer>
      <Tooltip title="Project email address" arrow>
        <IconButton
          size="medium"
          {...triggerProps}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}>
          <EmailIcon
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '1.25rem'
            }}
          />
        </IconButton>
      </Tooltip>

      <StyledMenu
        id="email-menu"
        {...menuProps}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Project Email
        </Typography>

        <EmailDisplayBox>
          <EmailText>{modifiedEmail}</EmailText>
          <Tooltip title="Copy email" arrow>
            <IconButton
              size="small"
              onClick={copyEmailToClipBoard}
              sx={{
                '&:hover': {
                  backgroundColor: 'transparent'
                }
              }}>
              <ContentCopyIcon
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '1rem'
                }}
              />
            </IconButton>
          </Tooltip>
        </EmailDisplayBox>

        <Divider sx={{ my: 1, borderColor: theme.palette.divider }} />

        <MenuItem
          component={Link}
          to={`/projects/${activeProject?.id}/mailbox`}
          onClick={() => menuProps.onClose()}
          target="_blank">
          <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
            Open Mailbox
          </Typography>
          <LaunchIcon
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '1rem'
            }}
          />
        </MenuItem>
      </StyledMenu>
    </EmailMenuContainer>
  );
};

export default EmailMenu;
